import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FileText, Shield, AlertCircle, Loader2, Check, ExternalLink } from 'lucide-react'
import toast from 'react-hot-toast'
import ModalPortal from '../ModalPortal'
import { createLogger } from '../../utils/logger'

const logger = createLogger('TermsReacceptanceModal')

const TermsReacceptanceModal = ({ isOpen, onAccepted }) => {
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const termsVersion = import.meta.env.VITE_TERMS_VERSION || '1.0'
  const privacyVersion = import.meta.env.VITE_PRIVACY_VERSION || '1.0'

  const handleAccept = async () => {
    // Validation
    const newErrors = {}
    if (!termsAccepted) {
      newErrors.terms = 'You must accept the Terms of Service to continue'
    }
    if (!privacyAccepted) {
      newErrors.privacy = 'You must accept the Privacy Policy to continue'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      toast.error('Please accept both documents to continue')
      return
    }

    setIsSubmitting(true)
    setErrors({})

    try {
      const response = await fetch('/api/compliance/accept-terms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          acceptTerms: true,
          acceptPrivacy: true
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update terms acceptance')
      }

      if (data.success) {
        toast.success('Terms accepted successfully!')

        // Fetch updated user data to get new terms/privacy versions
        const userResponse = await fetch('/api/auth/me', {
          credentials: 'include' // Ensure cookies are sent
        })

        if (!userResponse.ok) {
          throw new Error('Failed to fetch updated user data')
        }

        const userData = await userResponse.json()
        logger.debug('[TermsReacceptanceModal] Updated user data:', userData)

        // Call the onAccepted callback to update auth state and close modal
        // /api/auth/me returns {user: {...}, session: {...}}
        if (onAccepted && userData.user) {
          onAccepted(userData.user)
        } else {
          throw new Error('Invalid user data response')
        }
      } else {
        throw new Error(data.message || 'Failed to update terms acceptance')
      }
    } catch (error) {
      logger.error('Error accepting terms:', error)
      toast.error(error.message || 'Failed to accept terms. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCheckboxChange = (field) => {
    if (field === 'terms') {
      setTermsAccepted(!termsAccepted)
      if (errors.terms) {
        setErrors((prev) => ({ ...prev, terms: '' }))
      }
    } else if (field === 'privacy') {
      setPrivacyAccepted(!privacyAccepted)
      if (errors.privacy) {
        setErrors((prev) => ({ ...prev, privacy: '' }))
      }
    }
  }

  if (!isOpen) return null

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-75">
        {/* Modal content - full screen on mobile, constrained on desktop */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
                <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Updated Terms & Privacy Policy
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Please review and accept our updated policies to continue
                </p>
              </div>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* Important Notice */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                    Action Required
                  </h3>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    We've updated our Terms of Service and Privacy Policy to enhance your experience
                    and comply with the latest regulations. You must accept these updates to
                    continue using Deaveris.
                  </p>
                </div>
              </div>
            </div>

            {/* What's Changed Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                What's Changed?
              </h3>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Enhanced GDPR, CCPA, and COPPA compliance</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Improved data privacy and security measures</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Clarified your rights to access, export, and delete your data</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Updated communication preferences and opt-out options</span>
                </li>
              </ul>
            </div>

            {/* Document Links */}
            <div className="space-y-4 mb-6">
              {/* Terms Link */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Terms of Service
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Version {termsVersion}
                    </div>
                  </div>
                </div>
                <Link
                  to="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  Read Full Document
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>

              {/* Privacy Link */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      Privacy Policy
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Version {privacyVersion}
                    </div>
                  </div>
                </div>
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  Read Full Document
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Acceptance Checkboxes */}
            <div className="space-y-4">
              {/* Terms Checkbox */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={() => handleCheckboxChange('terms')}
                    disabled={isSubmitting}
                    className="mt-0.5 w-5 h-5 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  />
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none">
                    I have read and accept the updated{' '}
                    <strong className="text-gray-900 dark:text-white">Terms of Service</strong>
                  </span>
                </label>
                <AnimatePresence>
                  {errors.terms && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-8"
                    >
                      <AlertCircle className="w-3 h-3" />
                      <span>{errors.terms}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Privacy Checkbox */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={() => handleCheckboxChange('privacy')}
                    disabled={isSubmitting}
                    className="mt-0.5 w-5 h-5 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  />
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none">
                    I have read and accept the updated{' '}
                    <strong className="text-gray-900 dark:text-white">Privacy Policy</strong>
                  </span>
                </label>
                <AnimatePresence>
                  {errors.privacy && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-8"
                    >
                      <AlertCircle className="w-3 h-3" />
                      <span>{errors.privacy}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <button
              onClick={handleAccept}
              disabled={isSubmitting || !termsAccepted || !privacyAccepted}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Accepting...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Accept and Continue</span>
                </>
              )}
            </button>
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-3">
              You cannot use Deaveris without accepting these updated policies
            </p>
          </div>
        </motion.div>
      </div>
    </ModalPortal>
  )
}

export default TermsReacceptanceModal
